/**
 * Draws heading squiggle underlines when they scroll into view.
 *
 * Deliberately simple: one observer created on demand, no module-scope caching,
 * and a safety net that draws anything still undrawn shortly after load. An
 * earlier version cached the observer at module scope and silently never fired
 * in the storefront, so this trades a little elegance for behaviour I can verify.
 */

const SELECTOR = '.squiggle-underline:not(.squiggle-underline--static):not(.squiggle-underline--drawn)';
const DRAWN = 'squiggle-underline--drawn';

function draw(element) {
  element.classList.add(DRAWN);
}

function observe(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const elements = Array.prototype.slice.call(scope.querySelectorAll(SELECTOR));
  if (elements.length === 0) return;

  const reduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduced || typeof window.IntersectionObserver !== 'function') {
    elements.forEach(draw);
    return;
  }

  const io = new IntersectionObserver(
    function (entries, self) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        draw(entry.target);
        self.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -5% 0px', threshold: 0.01 }
  );

  elements.forEach(function (element) {
    io.observe(element);
  });

  // Safety net. If the observer has not reported within a couple of seconds -
  // which is what happened on the storefront before - draw whatever is already
  // on screen rather than leaving headings permanently blank.
  window.setTimeout(function () {
    elements.forEach(function (element) {
      if (element.classList.contains(DRAWN)) return;
      const box = element.getBoundingClientRect();
      const onScreen = box.top < window.innerHeight && box.bottom > 0;
      if (onScreen) {
        draw(element);
        io.unobserve(element);
      }
    });
  }, 2000);
}

function init() {
  observe(document);
  // Run again after first paint; Horizon hydrates some sections late.
  window.requestAnimationFrame(function () {
    observe(document);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

window.addEventListener('load', function () {
  observe(document);
});

// Sections re-render on every change in the theme editor and on section
// hydration in the storefront, so re-scan for newly injected headings.
document.addEventListener('shopify:section:load', function (event) {
  observe(event.target && event.target.querySelectorAll ? event.target : document);
});

export { observe };
